import { Alert } from 'src/components/common/AlertMessage/AlertMessage';
import { Add20Regular } from '@fluentui/react-icons';
import { useState, useEffect } from 'react';
import { useBoolean } from '@fluentui/react-hooks';
import { NewRequestSection } from 'src/components/Timeoff/NewRequestSection/NewRequestSection';
import { CalendarWidget } from 'src/components/Timeoff/widgets/CalendarWidget/CalendarWidget';
import { RequestDetails } from 'src/components/Timeoff/widgets/RequestDetailsWidget/RequestDetails';
import { RequestsWidget } from 'src/components/Timeoff/widgets/RequestsWidget/RequestsWidget';
import { useGetUserRequests } from 'src/components/Timeoff/widgets/RequestsWidget/hooks/useGetUserRequests';
import { SummaryWidget } from 'src/components/Timeoff/widgets/SummaryWidget/SummaryWidget';
import { useEmployeeVacationDays } from 'src/tabs/hooks/useSummaryInfo';
import { TeamWidget } from 'src/components/Timeoff/widgets/TeamWidget/TeamWidget';
import { ColorCodedEmployee } from 'src/components/Timeoff/widgets/TeamWidget/TeamWidgetSection';
import { AccordionCollapsibleMobile } from 'src/components/common/AccordionCollapsibleMobile/AccordionCollapsibleMobile';
import { useAlertMessage } from 'src/hooks/useAlertMessage';
import { TimeOffButton } from 'src/styles/globalStyles/buttonStyles.styled';
import { analyticsEventSend } from 'src/utils/analytics/analyticsEventSend';
import { StatusEnum } from 'src/utils/mappers/checkStatusCode';
import { useGetAllUsers } from './hooks/useGetAllUsers';
import { useGetHolidays } from './hooks/useGetHolidays';
import { useRequestFilters } from './hooks/useRequestFilters';
import { useVacationTypes } from './hooks/useVacationTypes';
import {
  MainContainer,
  TabContainer,
  WidgetsContainer,
} from './styles/Tab.styled';
import { TimeoffTabStyles } from 'src/styles/TimeoffTabStyles';
import axios from 'axios';
import {
  avatarBackgroundColors,
  avatarColors,
} from 'src/components/Timeoff/TeamVacationCircle/styles/TeamVacationCircle.styled';

export const Timeoff = () => {
  const currentYear = new Date().getFullYear();
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth());
  const [groups, setGroups] = useState<{ [k in string]: string }>({});
  const [groupUsersEmails, setGroupUsersEmails] = useState<Array<string>>([]);
  const {
    filterDirectionHandler,
    filterDirectionResetHandler,
    clearFilterDateHandler,
    clearVacationStatusHandler,
    clearVacationTypeHandler,
    filterDateYearAndMonthHandler,
    filterDate,
    filterDateHandler,
    vacationStatus,
    vacationStatusHandler,
    vacationType,
    vacationTypeHandler,
    filterDateType,
    filterDirection,
    sortBy,
  } = useRequestFilters(selectedYear);
  const {
    vacationPeriod,
    getSummary,
    loadingVacationPeriod,
    vacationPeriodNextYear,
    vacationPeriodPrevYear,
  } = useEmployeeVacationDays(selectedYear);
  const { vacationTypes, mappedVacationTypes } = useVacationTypes();
  const { users } = useGetAllUsers();
  const [team, setTeam] = useState<Array<ColorCodedEmployee>>([]);
  const [requestId, setRequestId] = useState(null);
  const { holidays } = useGetHolidays();
  const secureHolidays = holidays.length > 0 ? holidays : [];
  const {
    showAlert,
    hideAlert,
    requestStatusAppearance,
    requestStatusMessage,
  } = useAlertMessage();
  const { requests, getUserRequests, loadingRequest } = useGetUserRequests(
    vacationStatus,
    vacationType,
    vacationTypes,
    filterDate,
    filterDateType,
    filterDirection,
    selectedYear,
    sortBy
  );
  const mappedRequests = requests
    ? requests.map((request) => {
        return {
          ...request,
          statuscode:
            request.statuscode === StatusEnum.NEW
              ? StatusEnum.WAITING_FOR_APPROVAL
              : request.statuscode,
        };
      })
    : [];

  const [
    isRequestPopupVisible,
    { setTrue: showRequestPopup, setFalse: hideRequestPopup },
  ] = useBoolean(false);
  const [isPopupVisible, { setTrue: showPopup, setFalse: hidePopup }] =
    useBoolean(false);
  const [hideEmployees, setHideEmployees] = useState<boolean>(false);

  const hideEmployeeHandler = () => {
    setHideEmployees((prev) => !prev);
  };

  const incrementYear = () => {
    setSelectedYear((prev) => prev + 1);
  };

  const decrementYear = () => {
    setSelectedYear((prev) => prev - 1);
  };

  const incrementMonth = () => {
    if (selectedMonth === 11) {
      setSelectedMonth(0);
      incrementYear();
      return;
    }
    setSelectedMonth((prev) => prev + 1);
  };

  const decrementMonth = () => {
    if (selectedMonth === 0) {
      setSelectedMonth(11);
      decrementYear();
      return;
    }
    setSelectedMonth((prev) => prev - 1);
  };

  const requestIdHandler = (id: string) => {
    setRequestId(id);
  };

  const fetchTeamCache = async () => {
    const cachedTeam = await axios.get('/api/employees/team-cache');
    const ids = new Set(cachedTeam.data);
    const matchingIds = users.filter((user) => ids.has(user.new_employeeid));
    const parsedMatchingIds = matchingIds.map(
      (teamMember, index): ColorCodedEmployee => ({
        ...teamMember,
        backgroundPersona: avatarBackgroundColors[index],
        colorPersona: avatarColors[index],
      })
    );

    setTeam((prev) => {
      const existingIds = new Set(prev.map((member) => member.new_employeeid));
      const newMembers = parsedMatchingIds.filter(
        (member) => !existingIds.has(member.new_employeeid)
      );
      return [...prev, ...newMembers];
    });
  };

  const getGroupUsers = async (groupId: string) => {
    const groupUsers = await axios.get(`api/groups/${groupId}`);
    const groupUsersEmails = groupUsers.data.map(
      (user: { mail: string }) => user.mail
    );
    setGroupUsersEmails(groupUsersEmails);
  };

  useEffect(() => {
    getUserRequests();
  }, [selectedYear]);

  useEffect(() => {
    (async () => {
      const response = await axios.get('api/groups');
      setGroups(response.data);
    })();
  }, []);

  useEffect(() => {
    if (!users || !users.length) return;
    fetchTeamCache();
  }, [users]);

  return (
    <MainContainer>
      {requestStatusMessage && (
        <Alert
          overlay={true}
          requestStatusAppearance={requestStatusAppearance}
          requestStatusMessage={requestStatusMessage}
          handleClose={hideAlert}
        />
      )}
      <TabContainer id="time-off">
        <TimeoffTabStyles />

        <CalendarWidget
          loadingVacationPeriod={loadingVacationPeriod}
          vacationPeriodNextYear={vacationPeriodNextYear}
          vacationPeriodPrevYear={vacationPeriodPrevYear}
          hideEmployees={hideEmployees}
          year={selectedYear}
          decrementMonth={decrementMonth}
          incrementMonth={incrementMonth}
          incrementYear={incrementYear}
          decrementYear={decrementYear}
          holidays={secureHolidays}
          selectedMonth={selectedMonth}
          team={team}
          currentUserRequests={mappedRequests}
          vacationTypes={vacationTypes}
        ></CalendarWidget>
        <WidgetsContainer>
          <TimeOffButton
            appearance={'primary'}
            onClick={() => {
              showPopup();
              analyticsEventSend(
                'Request Time-off popup opened',
                'Request Time-off - Button'
              );
            }}
            disabled={currentYear > selectedYear}
          >
            <Add20Regular />
            Request Time-off
          </TimeOffButton>
          <SummaryWidget
            vacationPeriod={vacationPeriod}
            loadingVacationPeriod={loadingVacationPeriod}
          />
          <AccordionCollapsibleMobile title="Requests">
            <RequestsWidget
              mappedVacationTypes={mappedVacationTypes}
              requests={mappedRequests}
              getUserRequests={getUserRequests}
              showRequestPopup={showRequestPopup}
              requestIdHandler={requestIdHandler}
              loadingRequest={loadingRequest}
              vacationStatusHandler={vacationStatusHandler}
              vacationTypeHandler={vacationTypeHandler}
              filterDateHandler={filterDateHandler}
              clearFilterDateHandler={clearFilterDateHandler}
              clearVacationStatusHandler={clearVacationStatusHandler}
              clearVacationTypeHandler={clearVacationTypeHandler}
              vacationStatus={vacationStatus}
              filterDateType={filterDateType}
              filterDate={filterDate}
              vacationType={vacationType}
              filterDateTypeHandler={filterDateYearAndMonthHandler}
              filterDateYearAndMonthHandler={filterDateYearAndMonthHandler}
              filterDirection={filterDirection}
              filterDirectionHandler={filterDirectionHandler}
              filterDirectionResetHandler={filterDirectionResetHandler}
              sortBy={sortBy}
            />
          </AccordionCollapsibleMobile>
          <AccordionCollapsibleMobile title="Team">
            <TeamWidget
              hideEmployees={hideEmployees}
              hideEmployeeHandler={hideEmployeeHandler}
              setShowMessage={showAlert}
              users={users}
              setTeam={setTeam}
              team={team}
              groups={groups}
              getGroupUsers={getGroupUsers}
              groupUsersEmails={groupUsersEmails}
            />
          </AccordionCollapsibleMobile>
        </WidgetsContainer>
        <RequestDetails
          getSummary={getSummary}
          isRequestPopupVisible={isRequestPopupVisible}
          hideRequestPopup={hideRequestPopup}
          requestId={requestId}
          vacationTypes={vacationTypes}
          selectedYear={selectedYear}
          getUserRequests={getUserRequests}
          vacationType={vacationType}
          vacationStatus={vacationStatus}
        />
        <NewRequestSection
          mappedVacationTypes={mappedVacationTypes}
          vacationPeriod={vacationPeriod}
          selectedYear={selectedYear}
          vacationPeriodNextYear={vacationPeriodNextYear}
          isPopupVisible={isPopupVisible}
          showPopup={showPopup}
          hidePopup={hidePopup}
          setShowMessage={showAlert}
          holidays={holidays}
          vacationTypes={vacationTypes}
          getUserRequests={getUserRequests}
          getSummary={getSummary}
          vacationType={vacationType}
          vacationStatus={vacationStatus}
        />
      </TabContainer>
    </MainContainer>
  );
};
